/*
 * Transient messages.
 *
 * An error toast stays twice as long as a success one, and is announced
 * assertively to a screen reader — "your order was rejected" is not something
 * to find out about by noticing a box that has already gone.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

type Kind = "info" | "success" | "error";

interface Message {
  id: number;
  kind: Kind;
  text: string;
}

interface Toaster {
  show: (text: string, kind?: Kind) => void;
  ok: (text: string) => void;
  fail: (error: unknown) => void;
}

const ToastContext = createContext<Toaster | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<Message[]>([]);
  const nextId = useRef(1);

  const show = useCallback((text: string, kind: Kind = "info") => {
    const id = nextId.current++;
    setMessages((current) => [...current, { id, kind, text }]);
    window.setTimeout(
      () => setMessages((current) => current.filter((m) => m.id !== id)),
      kind === "error" ? 7000 : 3500,
    );
  }, []);

  const value = useMemo<Toaster>(
    () => ({
      show,
      ok: (text: string) => show(text, "success"),
      // The server writes its errors for the reader; this shows that sentence
      // rather than inventing one of its own.
      fail: (error: unknown) =>
        show(error instanceof Error ? error.message : "Something went wrong.", "error"),
    }),
    [show],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toasts">
        {messages.map((m) => (
          <div
            key={m.id}
            className={`toast ${m.kind}`}
            role={m.kind === "error" ? "alert" : "status"}
            aria-live={m.kind === "error" ? "assertive" : "polite"}
          >
            {m.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): Toaster {
  const value = useContext(ToastContext);
  if (!value) throw new Error("useToast must be used inside <ToastProvider>");
  return value;
}
